import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { formatDollars } from '@/lib/format'

const DEFAULT_SUGGESTED_CENTS = [2500, 5000, 10_000, 25_000]

export type DonationInterval = 'one_time' | 'monthly'

type Props = {
  amountCents: number | null
  onChange: (cents: number | null) => void
  suggested?: number[]
  // When provided, renders a one-time / monthly segmented toggle above the
  // amount grid. Omit it (e.g. the ticket+donation addon step) to keep the
  // picker one-time only.
  interval?: DonationInterval
  onIntervalChange?: (interval: DonationInterval) => void
}

// Mirrors CheckoutController's MIN/MAX_DONATION_CENTS. Custom inputs are
// dollars-with-cents; convert to integer cents and reject anything outside
// the server-side bounds so the picker matches the API contract.
function parseCustomAmount(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const num = Number(trimmed)
  if (!Number.isFinite(num) || num <= 0) return null
  const cents = Math.round(num * 100)
  if (cents < 100 || cents > 9_999_999) return null
  return cents
}

const INTERVAL_OPTIONS: Array<{ value: DonationInterval; label: string }> = [
  { value: 'one_time', label: 'One-time' },
  { value: 'monthly', label: 'Monthly' },
]

export default function DonationPicker({
  amountCents,
  onChange,
  suggested = DEFAULT_SUGGESTED_CENTS,
  interval,
  onIntervalChange,
}: Props) {
  const [customAmount, setCustomAmount] = useState(() =>
    amountCents != null && !suggested.includes(amountCents) ? (amountCents / 100).toString() : '',
  )
  const selectedChip = suggested.includes(amountCents ?? -1) ? amountCents : null
  const showIntervalToggle = interval != null && onIntervalChange != null

  return (
    <div>
      {showIntervalToggle && (
        <div
          className="mb-3 grid grid-cols-2 gap-2"
          role="group"
          aria-label="Donation frequency"
        >
          {INTERVAL_OPTIONS.map(({ value, label }) => {
            const active = interval === value
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                onClick={() => onIntervalChange(value)}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-black/10 bg-background hover:border-primary/40'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Suggested donation amounts">
        {suggested.map((cents) => {
          const active = selectedChip === cents
          return (
            <button
              key={cents}
              type="button"
              aria-pressed={active}
              onClick={() => {
                onChange(cents)
                setCustomAmount('')
              }}
              className={`rounded-lg border px-3 py-2 text-base font-semibold tabular-nums transition ${
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-black/10 bg-background hover:border-primary/40'
              }`}
            >
              {formatDollars(cents)}
            </button>
          )
        })}
      </div>

      <Input
        type="number"
        inputMode="decimal"
        step="0.01"
        min={1}
        placeholder="Other amount"
        value={customAmount}
        onChange={(e) => {
          const next = e.target.value
          setCustomAmount(next)
          onChange(next ? parseCustomAmount(next) : null)
        }}
        className="mt-3 bg-background"
        aria-label="Custom donation amount"
      />
    </div>
  )
}
